import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PaymentRequestListItem,
  getMyPaymentRequests,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import {
  PAYMENT_REQUEST_STATUS_FILTER_OPTIONS,
  PaymentRequestStatus,
  getPaymentRequestStatus,
  getPaymentRequestStatusLabel,
} from '../utils/paymentRequestStatus';
import { SortState, compareValues, formatContractSumDisplay, formatDateShort, toggleSort } from '../utils/table';
import '../styles/DataPage.css';
import './PaymentRequestsViewPage.css';

type SortKey =
  | 'request_date'
  | 'task_id'
  | 'task_short_name'
  | 'planfact_article'
  | 'note'
  | 'requestor_name'
  | 'approver_name'
  | 'approved_at'
  | 'issued_at'
  | 'rejected_at'
  | 'rejected_reason'
  | 'amount'
  | 'status';

type FilterMenuKey = 'article' | 'requestor' | 'approver' | 'status' | null;

function parseAmountBound(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

function displayValue(value: string | null | undefined): string {
  return value?.trim() ? value : '—';
}

function statusClassName(status: PaymentRequestStatus): string {
  return `payment-request-status payment-request-status--${status}`;
}

const PaymentRequestsViewPage: React.FC = () => {
  const { can } = useAuth();
  const canView = can('OUTCOME_REQUEST', 'VIEW');

  const [rows, setRows] = useState<PaymentRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<FilterMenuKey>(null);
  const [searchText, setSearchText] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [filterArticles, setFilterArticles] = useState<string[]>([]);
  const [filterRequestors, setFilterRequestors] = useState<string[]>([]);
  const [filterApprovers, setFilterApprovers] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<PaymentRequestStatus[]>([]);
  const [filterRequestDateFrom, setFilterRequestDateFrom] = useState('');
  const [filterRequestDateTo, setFilterRequestDateTo] = useState('');
  const [filterIssuedDateFrom, setFilterIssuedDateFrom] = useState('');
  const [filterIssuedDateTo, setFilterIssuedDateTo] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: 'issued_at',
    direction: 'desc',
  });

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canView) {
      setLoading(false);
      setRows([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await getMyPaymentRequests();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const articleFilterOptions = useMemo(
    () => [...new Set(rows.map((row) => row.planfact_article))].sort((a, b) => a.localeCompare(b, 'ru')),
    [rows],
  );

  const requestorFilterOptions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.requestor_name || '—'))].sort((a, b) =>
        a.localeCompare(b, 'ru'),
      ),
    [rows],
  );

  const approverFilterOptions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.approver_name || '—'))].sort((a, b) =>
        a.localeCompare(b, 'ru'),
      ),
    [rows],
  );

  const statusFilterOptions = useMemo(
    () =>
      PAYMENT_REQUEST_STATUS_FILTER_OPTIONS.map((status) => ({
        value: status,
        label: getPaymentRequestStatusLabel(status),
      })),
    [],
  );

  const hasActiveFilters = useMemo(
    () =>
      searchText.trim() !== ''
      || taskIdFilter.trim() !== ''
      || filterArticles.length > 0
      || filterRequestors.length > 0
      || filterApprovers.length > 0
      || filterStatuses.length > 0
      || filterRequestDateFrom.trim() !== ''
      || filterRequestDateTo.trim() !== ''
      || filterIssuedDateFrom.trim() !== ''
      || filterIssuedDateTo.trim() !== ''
      || minAmountFilter.trim() !== ''
      || maxAmountFilter.trim() !== '',
    [
      searchText,
      taskIdFilter,
      filterArticles,
      filterRequestors,
      filterApprovers,
      filterStatuses,
      filterRequestDateFrom,
      filterRequestDateTo,
      filterIssuedDateFrom,
      filterIssuedDateTo,
      minAmountFilter,
      maxAmountFilter,
    ],
  );

  const clearFilters = (): void => {
    setSearchText('');
    setTaskIdFilter('');
    setFilterArticles([]);
    setFilterRequestors([]);
    setFilterApprovers([]);
    setFilterStatuses([]);
    setFilterRequestDateFrom('');
    setFilterRequestDateTo('');
    setFilterIssuedDateFrom('');
    setFilterIssuedDateTo('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseAmountBound(minAmountFilter);
    const sumToN = parseAmountBound(maxAmountFilter);

    return rows.filter((row) => {
      const status = getPaymentRequestStatus(row);
      const statusLabel = getPaymentRequestStatusLabel(status);
      const requestorName = row.requestor_name || '—';
      const approverName = row.approver_name || '—';
      const searchable = [
        row.task_id,
        row.task_short_name,
        row.planfact_article,
        row.note,
        requestorName,
        approverName,
        row.rejected_reason,
        statusLabel,
        row.request_date,
        row.approved_at,
        row.issued_at,
        row.rejected_at,
        String(row.amount),
        formatContractSumDisplay(row.amount),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = Number(row.amount) || 0;

      return (
        (!text || searchable.includes(text))
        && (!taskIdFilter.trim() || row.task_id.toLowerCase().includes(taskIdFilter.trim().toLowerCase()))
        && (filterArticles.length === 0 || filterArticles.includes(row.planfact_article))
        && (filterRequestors.length === 0 || filterRequestors.includes(requestorName))
        && (filterApprovers.length === 0 || filterApprovers.includes(approverName))
        && (filterStatuses.length === 0 || filterStatuses.includes(status))
        && dateInRange(row.request_date, filterRequestDateFrom, filterRequestDateTo)
        && dateInRange(row.issued_at, filterIssuedDateFrom, filterIssuedDateTo)
        && (sumFromN === null || amount >= sumFromN)
        && (sumToN === null || amount <= sumToN)
      );
    });
  }, [
    rows,
    searchText,
    taskIdFilter,
    filterArticles,
    filterRequestors,
    filterApprovers,
    filterStatuses,
    filterRequestDateFrom,
    filterRequestDateTo,
    filterIssuedDateFrom,
    filterIssuedDateTo,
    minAmountFilter,
    maxAmountFilter,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((left, right) => {
      const leftStatus = getPaymentRequestStatus(left);
      const rightStatus = getPaymentRequestStatus(right);
      const leftMap: Record<SortKey, string | number | undefined> = {
        request_date: left.request_date,
        task_id: left.task_id,
        task_short_name: left.task_short_name ?? '',
        planfact_article: left.planfact_article,
        note: left.note ?? '',
        requestor_name: left.requestor_name ?? '',
        approver_name: left.approver_name ?? '',
        approved_at: left.approved_at ?? '',
        issued_at: left.issued_at ?? '',
        rejected_at: left.rejected_at ?? '',
        rejected_reason: left.rejected_reason ?? '',
        amount: left.amount,
        status: getPaymentRequestStatusLabel(leftStatus),
      };
      const rightMap: Record<SortKey, string | number | undefined> = {
        request_date: right.request_date,
        task_id: right.task_id,
        task_short_name: right.task_short_name ?? '',
        planfact_article: right.planfact_article,
        note: right.note ?? '',
        requestor_name: right.requestor_name ?? '',
        approver_name: right.approver_name ?? '',
        approved_at: right.approved_at ?? '',
        issued_at: right.issued_at ?? '',
        rejected_at: right.rejected_at ?? '',
        rejected_reason: right.rejected_reason ?? '',
        amount: right.amount,
        status: getPaymentRequestStatusLabel(rightStatus),
      };
      return compareValues(leftMap[sortState.key], rightMap[sortState.key], sortState.direction);
    });
    return sorted;
  }, [filteredRows, sortState]);

  const totalAmount = useMemo(
    () => sortedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [sortedRows],
  );

  const handleSort = (key: SortKey): void => {
    setSortState((prev) => toggleSort(prev, key));
  };

  const colgroup = (
    <colgroup>
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--date" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--task-id" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--task-name" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--article" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--note" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--person" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--person" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--date" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--date" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--date" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--reason" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--amount" />
      <col className="payment-requests-view-page__colgrid payment-requests-view-page__colgrid--status" />
    </colgroup>
  );

  return (
    <div className="data-page tasks-page payment-requests-view-page">
      {!canView ? (
        <div className="empty-state">Нет доступа (требуется OUTCOME_REQUEST VIEW).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Это мои заявки на выдачу денежных средств</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <label>
                    ID задачи
                    <input value={taskIdFilter} onChange={(e) => setTaskIdFilter(e.target.value)} />
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья"
                      options={articleFilterOptions}
                      selected={filterArticles}
                      onSelectedChange={setFilterArticles}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'article'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'article' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Запрашивающий"
                      options={requestorFilterOptions}
                      selected={filterRequestors}
                      onSelectedChange={setFilterRequestors}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'requestor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'requestor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Согласующий"
                      options={approverFilterOptions}
                      selected={filterApprovers}
                      onSelectedChange={setFilterApprovers}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'approver'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'approver' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статус"
                      options={statusFilterOptions.map((item) => item.label)}
                      selected={filterStatuses.map((status) => getPaymentRequestStatusLabel(status))}
                      onSelectedChange={(selected) => {
                        const next = statusFilterOptions
                          .filter((item) => selected.includes(item.label))
                          .map((item) => item.value);
                        setFilterStatuses(next);
                      }}
                      formatOption={(value) => value}
                      isOpen={filterMenuOpen === 'status'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'status' : null)}
                    />
                  </div>
                  <label>
                    Дата заявки с
                    <input
                      type="date"
                      value={filterRequestDateFrom}
                      onChange={(e) => setFilterRequestDateFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата заявки по
                    <input
                      type="date"
                      value={filterRequestDateTo}
                      onChange={(e) => setFilterRequestDateTo(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата выплаты с
                    <input
                      type="date"
                      value={filterIssuedDateFrom}
                      onChange={(e) => setFilterIssuedDateFrom(e.target.value)}
                    />
                  </label>
                  <label>
                    Дата выплаты по
                    <input
                      type="date"
                      value={filterIssuedDateTo}
                      onChange={(e) => setFilterIssuedDateTo(e.target.value)}
                    />
                  </label>
                  <div className="filters-sum-block">
                    <div className="filters-sum-block__title">Сумма</div>
                    <div className="filters-sum-block__row">
                      <input
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {loading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table payment-requests-view-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          <th className="sortable" onClick={() => handleSort('request_date')} role="button">
                            Дата заявки
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_id')} role="button">
                            ID задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('task_short_name')} role="button">
                            Название задачи
                          </th>
                          <th className="sortable" onClick={() => handleSort('planfact_article')} role="button">
                            Статья
                          </th>
                          <th className="sortable" onClick={() => handleSort('note')} role="button">
                            Примечание
                          </th>
                          <th className="sortable" onClick={() => handleSort('requestor_name')} role="button">
                            Запрашивающий
                          </th>
                          <th className="sortable" onClick={() => handleSort('approver_name')} role="button">
                            Согласующий
                          </th>
                          <th className="sortable" onClick={() => handleSort('approved_at')} role="button">
                            Дата согласования
                          </th>
                          <th className="sortable" onClick={() => handleSort('issued_at')} role="button">
                            Дата выплаты
                          </th>
                          <th className="sortable" onClick={() => handleSort('rejected_at')} role="button">
                            Дата отклонения
                          </th>
                          <th className="sortable" onClick={() => handleSort('rejected_reason')} role="button">
                            Причина отклонения
                          </th>
                          <th className="sortable col-num" onClick={() => handleSort('amount')} role="button">
                            Сумма
                          </th>
                          <th className="sortable" onClick={() => handleSort('status')} role="button">
                            Статус
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => {
                            const status = getPaymentRequestStatus(row);
                            return (
                              <tr key={row.id}>
                                <td>{formatDateShort(row.request_date)}</td>
                                <td>{row.task_id}</td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">
                                    {displayValue(row.task_short_name)}
                                  </div>
                                </td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">{row.planfact_article}</div>
                                </td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">{displayValue(row.note)}</div>
                                </td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">
                                    {displayValue(row.requestor_name)}
                                  </div>
                                </td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">
                                    {displayValue(row.approver_name)}
                                  </div>
                                </td>
                                <td>{row.approved_at ? formatDateShort(row.approved_at) : '—'}</td>
                                <td>{row.issued_at ? formatDateShort(row.issued_at) : '—'}</td>
                                <td>{row.rejected_at ? formatDateShort(row.rejected_at) : '—'}</td>
                                <td>
                                  <div className="payment-requests-view-page__clamp-2">
                                    {displayValue(row.rejected_reason)}
                                  </div>
                                </td>
                                <td className="col-num payment-requests-view-page__td-amount">
                                  {formatContractSumDisplay(row.amount)}
                                </td>
                                <td>
                                  <span className={statusClassName(status)}>
                                    {getPaymentRequestStatusLabel(status)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer payment-requests-view-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedRows.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num payment-requests-view-page__td-amount">
                            {formatContractSumDisplay(totalAmount)}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentRequestsViewPage;
